/**
 * The base layer: what every element inside the design system's root looks like
 * before anybody styles anything.
 *
 * It is scoped to `.ui-root` rather than to `:root`, and that is the whole
 * reason the system can be dropped into an application that has its own CSS: it
 * resets and re-types only the subtree the host hands over. Nothing here
 * escapes that class, and `tests/designSystem.test.ts` asserts it.
 *
 * Everything below is either a value from `tokens.ts` or a decision about
 * *shape*, and shape is the design direction (docs/28 §1): monospace, dark,
 * square corners, 1px borders, no blur, no shadow that costs a compositor
 * layer. The direction is terminal-adjacent, and terminals do not have
 * rounded corners.
 */

/**
 * The typographic defaults, and the element resets.
 *
 * These used to share one rule with the token declarations. They are two rules
 * on the same selector now — same specificity, adjacent in source order,
 * disjoint property sets, so the cascade is bit-for-bit what it was — because a
 * single rule that both *declares* the vocabulary and *spends* it is precisely
 * the seam this whole exercise is about. The first rule is the design system's
 * data; the second is its opinion.
 */
export const BASE_CSS = /* css */ `
.ui-root {
  font-family: var(--ui-font);
  font-size: var(--ui-fs);
  line-height: 1.35;
  color: var(--ui-fg);
  letter-spacing: .04em;
  /* Tabular figures are not a flourish. Half of what this interface prints is a
     number that changes while you are looking at it, and proportional digits
     make a counter jitter horizontally on every tick. */
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
}

.ui-root *, .ui-root *::before, .ui-root *::after { box-sizing: border-box; }
/* Recycling pools hide their spare elements with [hidden]; flex and grid both
   override the UA rule for it, so restate it once with weight. Any list that
   reuses its DOM rather than rebuilding it needs this. */
.ui-root [hidden] { display: none !important; }

.ui-root button, .ui-root input, .ui-root select, .ui-root textarea {
  font: inherit; letter-spacing: inherit; color: inherit;
  background: var(--ui-panel-2); border: 1px solid var(--ui-line);
  border-radius: 0; padding: 2px 6px; cursor: pointer;
}
.ui-root button:hover, .ui-root select:hover { border-color: var(--ui-line-hot); background: var(--ui-panel-3); }
.ui-root button:active { background: var(--ui-panel); }
.ui-root button[disabled] { opacity: .35; cursor: default; }
.ui-root input, .ui-root textarea { cursor: text; }
/* :focus-visible, not :focus — a mouse click on a button must not draw a ring,
   and a keyboard tab to it must. The offset is what keeps the ring off the
   1px border it would otherwise sit exactly on top of. */
.ui-root :focus-visible { outline: 1px solid var(--ui-accent); outline-offset: 1px; }
.ui-root ::selection { background: var(--ui-accent); color: var(--ui-ink-inv); }
`;
